import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #0f172a;
  padding: 2rem;
`;

const Card = styled.div`
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
  border-radius: 24px;
  padding: 3rem;
  width: 100%;
  max-width: 440px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
  animation: ${fadeIn} 0.6s ease-out;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin-bottom: 2rem;
`;

const LogoIcon = styled.div`
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: white;
  font-weight: 800;
`;

const LogoText = styled.span`
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
`;

const Title = styled.h1`
  color: white;
  font-size: 1.75rem;
  font-weight: 700;
  text-align: center;
  margin: 0 0 0.5rem 0;
`;

const Subtitle = styled.p`
  color: #94a3b8;
  text-align: center;
  margin: 0 0 2rem 0;
  font-size: 0.95rem;
  line-height: 1.6;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  color: #e2e8f0;
  font-size: 0.875rem;
  font-weight: 500;
`;

const Input = styled.input`
  padding: 0.875rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  color: white;
  font-size: 1rem;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    background: rgba(255, 255, 255, 0.08);
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
  }

  &::placeholder {
    color: #64748b;
  }
`;

const Button = styled.button`
  padding: 1rem;
  background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
  border: none;
  border-radius: 12px;
  color: white;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(59, 130, 246, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const BackLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  color: #94a3b8;
  text-decoration: none;
  font-size: 0.9rem;
  margin-top: 1.5rem;
  transition: color 0.2s;

  &:hover {
    color: #3b82f6;
  }
`;

const Message = styled.div<{ type: 'success' | 'error' | 'info' }>`
  padding: 1rem;
  border-radius: 12px;
  font-size: 0.9rem;
  text-align: center;
  background: ${props => {
    switch (props.type) {
      case 'success': return 'rgba(16, 185, 129, 0.15)';
      case 'error': return 'rgba(239, 68, 68, 0.15)';
      default: return 'rgba(59, 130, 246, 0.15)';
    }
  }};
  border: 1px solid ${props => {
    switch (props.type) {
      case 'success': return 'rgba(16, 185, 129, 0.3)';
      case 'error': return 'rgba(239, 68, 68, 0.3)';
      default: return 'rgba(59, 130, 246, 0.3)';
    }
  }};
  color: ${props => {
    switch (props.type) {
      case 'success': return '#10b981';
      case 'error': return '#ef4444';
      default: return '#3b82f6';
    }
  }};
`;

const SuccessIcon = styled.div`
  width: 64px;
  height: 64px;
  margin: 0 auto 1.5rem;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
`;

const ErrorIcon = styled.div`
  width: 64px;
  height: 64px;
  margin: 0 auto 1.5rem;
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
`;

const Spinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid rgba(255, 255, 255, 0.1);
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
  margin: 0 auto 1.5rem;
`;

const PasswordRequirements = styled.ul`
  margin: 0;
  padding: 0 0 0 1.25rem;
  color: #94a3b8;
  font-size: 0.85rem;
  line-height: 1.8;
`;

const ResetPasswordScreen: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState('');
  const [userInfo, setUserInfo] = useState<{ email: string; fullName: string } | null>(null);

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setTokenError('No reset token provided. Please request a new password reset.');
        setValidating(false);
        return;
      }

      try {
        const response = await axios.get(`${API_BASE_URL}/api/auth/reset-password/validate?token=${token}`);
        if (response.data.valid) {
          setTokenValid(true);
          setUserInfo({
            email: response.data.email,
            fullName: response.data.fullName
          });
        } else {
          setTokenError(response.data.error || 'Invalid or expired reset link.');
        }
      } catch (err) {
        setTokenError('Invalid or expired reset link. Please request a new password reset.');
      } finally {
        setValidating(false);
      }
    };

    validateToken();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API_BASE_URL}/api/auth/reset-password`, {
        token,
        newPassword
      });
      setSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (validating) {
    return (
      <Container>
        <Card>
          <Spinner />
          <Title>Validating Reset Link</Title>
          <Subtitle>Please wait while we verify your password reset link...</Subtitle>
        </Card>
      </Container>
    );
  }

  // Invalid token
  if (!tokenValid) {
    return (
      <Container>
        <Card>
          <ErrorIcon>❌</ErrorIcon>
          <Title>Invalid Reset Link</Title>
          <Subtitle>{tokenError}</Subtitle>
          <Button onClick={() => navigate('/forgot-password')}>
            Request New Reset Link
          </Button>
          <BackLink to="/login">
            ← Back to Login
          </BackLink>
        </Card>
      </Container>
    );
  }

  // Success state
  if (success) {
    return (
      <Container>
        <Card>
          <SuccessIcon>✓</SuccessIcon>
          <Title>Password Reset Successful!</Title>
          <Subtitle>
            Your password has been changed successfully. You can now login with your new password.
          </Subtitle>
          <Button onClick={() => navigate('/login')}>
            Go to Login
          </Button>
        </Card>
      </Container>
    );
  }

  // Reset form
  return (
    <Container>
      <Card>
        <Logo>
          <LogoIcon>🔑</LogoIcon>
          <LogoText>New Password</LogoText>
        </Logo>
        
        <Title>Create New Password</Title>
        <Subtitle>
          Hi {userInfo?.fullName}, enter your new password below.
        </Subtitle>

        <Form onSubmit={handleSubmit}>
          {error && <Message type="error">{error}</Message>}
          
          <InputGroup>
            <Label htmlFor="newPassword">New Password</Label>
            <Input
              id="newPassword"
              type="password"
              placeholder="Enter new password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              autoFocus
            />
          </InputGroup>

          <InputGroup>
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <Input
              id="confirmPassword"
              type="password"
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
          </InputGroup>

          <PasswordRequirements>
            <li>At least 12 characters long, with an uppercase letter, a digit and a special character (server policy)</li>
            <li>Use a mix of letters, numbers, and symbols for better security</li>
          </PasswordRequirements>

          <Button type="submit" disabled={loading || !newPassword || !confirmPassword}>
            {loading ? 'Resetting...' : 'Reset Password'}
          </Button>
        </Form>

        <BackLink to="/login">
          ← Back to Login
        </BackLink>
      </Card>
    </Container>
  );
};

export default ResetPasswordScreen;
